// types
import { DemandesFinancementActions, DemandesFinancementStatus, DemandesFinancementTags, SollicitationsTypes, isResultError, isResultSuccess, StoreModulesName, } from '../types';
/**
 * DemandesFinancement service
 */
class DemandesFinancementService {
    demandesFinancementAdapter;
    demandesFinancementFormatter;
    storeService;
    tenantId;
    constructor({ demandesFinancementAdapter, demandesFinancementFormatter, storeService, tenantId }) {
        this.demandesFinancementAdapter = demandesFinancementAdapter;
        this.demandesFinancementFormatter = demandesFinancementFormatter;
        this.storeService = storeService;
        this.tenantId = tenantId;
    }
    /**
     * Get a Demande Financement by reference
     */
    async getDemandeFinancement(reference, contribution) {
        const payload = await this.demandesFinancementAdapter.getDemandeFinancement(reference, contribution);
        if (isResultSuccess(payload)) {
            return payload.response;
        }
        throw payload.error ?? new Error('an unknown error occurred');
    }
    /**
     * Loads the count of demandes financement from the adapter service.
     *
     * @returns A promise that resolves when the count of demandes financement has been loaded.
     */
    async loadTotalDemandesFinancement() {
        // Get the totalDemandes store module
        const totalDemandesFinancementStore = this.storeService.getTotalDemandesFinancement();
        // If the store module is not already loading data
        if (!totalDemandesFinancementStore.loading()) {
            // Set the store module to loading mode
            totalDemandesFinancementStore.SET_LOADING(true);
            try {
                // Call the adapter service and wait for the response
                const payload = await this.demandesFinancementAdapter.countDemandesFinancement();
                // Set the response data on the store module
                totalDemandesFinancementStore.SET_DATA((isResultSuccess(payload) && payload.response) || 0);
            }
            catch (error) {
                // Set the error on the store module
                totalDemandesFinancementStore.SET_ERROR(error);
            }
            // Set the store module to not loading mode
            totalDemandesFinancementStore.SET_LOADING(false);
        }
    }
    /**
     * Loads the count of shared demandes financement from the adapter service.
     *
     * @returns A promise that resolves when the count of shared demandes financement has been loaded.
     */
    async loadTotalSharedDemandesFinancement() {
        const totalSharedDemandesFinancementStore = this.storeService.getTotalSharedDemandesFinancement();
        if (!totalSharedDemandesFinancementStore.loading()) {
            totalSharedDemandesFinancementStore.SET_LOADING(true);
            try {
                const payload = await this.demandesFinancementAdapter.countDemandesFinancementShared();
                totalSharedDemandesFinancementStore.SET_DATA((isResultSuccess(payload) && payload.response) || 0);
            }
            catch (error) {
                totalSharedDemandesFinancementStore.SET_ERROR(error);
            }
            totalSharedDemandesFinancementStore.SET_LOADING(false);
        }
    }
    /**
     * Loads the list of demandes financement from the adapter service.
     *
     * @param pagination - An object containing the "from" and "size" properties for pagination.
     * @param filters - Optional filters to apply on the list of demandes.
     * @returns A promise that resolves when the list of demandes financement has been loaded.
     */
    async loadDemandesFinancement(pagination, filters) {
        const demandesFinancementStore = this.storeService.getDemandesFinancement();
        const currentUser = this.storeService.getCurrentUser().data();
        const publicSettings = this.storeService.getPublicSettings().data();
        if (!demandesFinancementStore.loading()) {
            const totalDemandesFinancementStore = this.storeService.getTotalDemandesFinancement();
            const aggregationsStore = this.storeService.getAggregation();
            demandesFinancementStore.SET_LOADING(true);
            try {
                if (!currentUser)
                    throw new Error('the currentUser is not loaded in the store');
                if (!publicSettings)
                    throw new Error('publicSettings is not loaded in the store');
                const demandesFinancement = await this.demandesFinancementAdapter.getDemandesFinancement(pagination, !aggregationsStore.data(), null, filters);
                demandesFinancementStore.SET_DATA(isResultSuccess(demandesFinancement)
                    ? await this.demandesFinancementFormatter.formatDemandesFinancementList(demandesFinancement.response.data, currentUser, demandesFinancement.response.authorized, publicSettings)
                    : []);
                totalDemandesFinancementStore.SET_DATA((isResultSuccess(demandesFinancement) && demandesFinancement.response.meta.total) || 0);
                // if aggregations data for filter is not loaded
                if (!aggregationsStore.data()) {
                    aggregationsStore.SET_DATA((isResultSuccess(demandesFinancement) && demandesFinancement.response.aggregations) || null);
                }
            }
            catch (error) {
                demandesFinancementStore.SET_ERROR(error);
            }
            demandesFinancementStore.SET_LOADING(false);
        }
    }
    /**
     * Loads the list of shared demandes financement from the adapter service.
     */
    async loadSharedDemandesFinancement(pagination) {
        const sharedDemandesFinancementStore = this.storeService.getSharedDemandesFinancement();
        const currentUser = this.storeService.getCurrentUser().data();
        const publicSettings = this.storeService.getPublicSettings().data();
        if (!sharedDemandesFinancementStore.loading()) {
            const totalSharedDemandesStore = this.storeService.getTotalSharedDemandesFinancement();
            sharedDemandesFinancementStore.SET_LOADING(true);
            try {
                if (!currentUser)
                    throw new Error('the currentUser is not loaded in the store');
                if (!publicSettings)
                    throw new Error('publicSettings is not loaded in the store');
                const sharedDemandes = await this.demandesFinancementAdapter.getDemandesFinancementShared(pagination);
                sharedDemandesFinancementStore.SET_DATA(isResultSuccess(sharedDemandes)
                    ? await this.demandesFinancementFormatter.formatDemandesFinancementList(sharedDemandes.response.data, currentUser, sharedDemandes.response.authorized, publicSettings)
                    : []);
                totalSharedDemandesStore.SET_DATA((isResultSuccess(sharedDemandes) && sharedDemandes.response.meta.total) || 0);
            }
            catch (error) {
                sharedDemandesFinancementStore.SET_ERROR(error);
            }
            sharedDemandesFinancementStore.SET_LOADING(false);
        }
    }
    /**
     * Loads the list of latest shared demandes financement from the adapter service.
     */
    async loadLatestDemandesFinancement() {
        const latestDemandesFinancementStore = this.storeService.getLatestDemandesFinancement();
        const currentUser = this.storeService.getCurrentUser().data();
        const publicSettings = this.storeService.getPublicSettings().data();
        if (!latestDemandesFinancementStore.loading()) {
            const totalDemandesStore = this.storeService.getTotalDemandesFinancement();
            latestDemandesFinancementStore.SET_LOADING(true);
            try {
                if (!currentUser)
                    throw new Error('the currentUser is not loaded in the store');
                if (!publicSettings)
                    throw new Error('publicSettings is not loaded in the store');
                const demandes = await this.demandesFinancementAdapter.getDemandesFinancement({ from: 0, size: 3 }, false, 'date:desc');
                latestDemandesFinancementStore.SET_DATA(isResultSuccess(demandes)
                    ? await this.demandesFinancementFormatter.formatDemandesFinancementList(demandes.response.data, currentUser, demandes.response.authorized, publicSettings)
                    : []);
                totalDemandesStore.SET_DATA((isResultSuccess(demandes) && demandes.response.meta.total) || 0);
            }
            catch (error) {
                latestDemandesFinancementStore.SET_ERROR(error);
            }
            latestDemandesFinancementStore.SET_LOADING(false);
        }
    }
    /**
     * Loads the list of latest demandes financement from the adapter service.
     */
    async loadLatestSharedDemandesFinancement() {
        const latestSharedDemandesFinancementStore = this.storeService.getLatestSharedDemandesFinancement();
        const currentUser = this.storeService.getCurrentUser().data();
        const publicSettings = this.storeService.getPublicSettings().data();
        if (!latestSharedDemandesFinancementStore.loading()) {
            const totalSharedDemandesFinancementStore = this.storeService.getTotalSharedDemandesFinancement();
            latestSharedDemandesFinancementStore.SET_LOADING(true);
            try {
                if (!currentUser)
                    throw new Error('the currentUser is not loaded in the store');
                if (!publicSettings)
                    throw new Error('publicSettings is not loaded in the store');
                const sharedDemandes = await this.demandesFinancementAdapter.getDemandesFinancementShared({ from: 0, size: 3 }, 'date:desc');
                latestSharedDemandesFinancementStore.SET_DATA(isResultSuccess(sharedDemandes)
                    ? await this.demandesFinancementFormatter.formatDemandesFinancementList(sharedDemandes.response.data, currentUser, sharedDemandes.response.authorized, publicSettings)
                    : []);
                totalSharedDemandesFinancementStore.SET_DATA((isResultSuccess(sharedDemandes) && sharedDemandes.response?.meta.total) || 0);
            }
            catch (error) {
                latestSharedDemandesFinancementStore.SET_ERROR(error);
            }
            latestSharedDemandesFinancementStore.SET_LOADING(false);
        }
    }
    /**
     * Retrieve linked demandes financement using the adapter.
     */
    async loadLinkedDemandesFinancement(idCpo, idToExclude) {
        // Get the linkedDemandesStore store module
        const linkedDemandesFinancementStore = this.storeService.getLinkedDemandesFinancement();
        // If the store module is not already loading data
        if (!linkedDemandesFinancementStore.loading()) {
            // Set the store module to loading mode
            linkedDemandesFinancementStore.SET_LOADING(true);
            try {
                // Call the adapter service and wait for the response
                const result = await this.demandesFinancementAdapter.getLinkedDemandesFinancement(idCpo, idToExclude);
                if (isResultError(result)) {
                    throw result.error;
                }
                const currentUser = this.storeService.getCurrentUser().data();
                const publicSettings = this.storeService.getPublicSettings().data();
                if (!currentUser)
                    throw new Error('the currentUser is not loaded in the store');
                if (!publicSettings)
                    throw new Error('publicSettings is not loaded in the store');
                const linkedDemandes = await this.demandesFinancementFormatter.formatDemandesFinancementList(result.response.data ?? [], currentUser, [], publicSettings);
                // Set the response data on the store module
                linkedDemandesFinancementStore.SET_DATA(linkedDemandes);
            }
            catch (error) {
                // Set the error on the store module
                linkedDemandesFinancementStore.SET_ERROR(error);
            }
            // Set the store module to not loading mode
            linkedDemandesFinancementStore.SET_LOADING(false);
        }
    }
    /**
     * Update demande status with 'TRANSMITTED' value
     */
    transmitDemandeFinancement(reference) {
        const patch = [
            {
                op: 'replace',
                path: '/status',
                value: DemandesFinancementStatus.transmitted,
            },
        ];
        return this.demandesFinancementAdapter.patchDemandeFinancementByReference(reference, patch);
    }
    /**
     * Update demande status with 'SUPPORTED' value
     */
    supportDemandeFinancement(reference) {
        const patch = [
            {
                op: 'replace',
                path: '/status',
                value: DemandesFinancementStatus.supported,
            },
        ];
        return this.demandesFinancementAdapter.patchDemandeFinancementByReference(reference, patch);
    }
    /**
     * Update tag list when restrictions are updated
     */
    updateRestrictionsTag(demandeId, isRestricted, modules = [StoreModulesName.DEMANDES_FINANCEMENT, StoreModulesName.LATEST_DEMANDES_FINANCEMENT]) {
        // loop over demande list keys
        for (const module of modules) {
            // get the store module
            const store = this.storeService.get(module);
            // get the module data
            const data = store.data();
            // get demande financement to update
            const demande = data?.find(({ id }) => id === demandeId);
            // if the targeted demande is found
            if (demande) {
                // get the tag index
                const tagIndex = demande.tags?.findIndex((tag) => tag.id === DemandesFinancementTags.MANAGE_RIGHTS);
                if (isRestricted && tagIndex === -1) {
                    // if is restricted and the tag do not exist, create one
                    demande.tags?.push(this.demandesFinancementFormatter.generateRestrictedTag());
                }
                else if (!isRestricted && tagIndex !== -1) {
                    // if is not restricted and the tag exist, remove it
                    demande.tags?.splice(tagIndex, 1);
                }
            }
        }
    }
    /**
     * update shared tag and actions buttons in corespondig demande
     */
    updateDemandeFinancementSharings(demandeId, sharings, modules = [StoreModulesName.DEMANDES_FINANCEMENT, StoreModulesName.LATEST_DEMANDES_FINANCEMENT]) {
        // loop over demande list keys
        for (const module of modules) {
            // get the store module
            const store = this.storeService.get(module);
            // get the module data
            const data = store.data();
            // get demande financement to update
            const demande = data?.find(({ id }) => id === demandeId);
            // if the targeted demande is found
            if (demande) {
                // if sharings is not empty
                if (sharings.length > 0) {
                    // get the demande sharing tag index
                    const tagIndex = demande.tags?.findIndex((e) => e.id === DemandesFinancementTags.SHARED);
                    // if sharing tag is found
                    if (tagIndex === -1) {
                        // if the sharing tag dosen't exist, generate one
                        demande.tags?.push(this.demandesFinancementFormatter.formatTag('shared', 'users', sharings.length));
                    }
                    else {
                        const tag = demande.tags[tagIndex];
                        if (tag?.badge) {
                            // update the badge value of the sharing tag
                            tag.badge.value = sharings.length;
                        }
                    }
                    // update actions buttons
                    const accessActionIndex = demande.actions.findIndex((e) => e.id === DemandesFinancementActions.ACCESS);
                    if (accessActionIndex === -1) {
                        // add access action if missing
                        demande.actions.push(this.demandesFinancementFormatter.formatAction(DemandesFinancementActions.ACCESS, 'chevron-right', {
                            variant: 'secondary',
                        }));
                    }
                }
                else {
                    // if sharing is an empty array
                    const tagIndex = demande.tags?.findIndex((e) => e.id === DemandesFinancementTags.SHARED);
                    if (tagIndex >= 0) {
                        // remove the sharing tag if exist
                        demande.tags?.splice(tagIndex, 1);
                    }
                    // update actions buttons
                    const accessActionIndex = demande.actions.findIndex((e) => e.id === DemandesFinancementActions.ACCESS);
                    if (accessActionIndex >= 0) {
                        // remove access action
                        demande.actions.splice(accessActionIndex, 1);
                    }
                }
            }
        }
    }
    /**
     * Update 'share-contribution' type action in corresponding demande contribution
     */
    updateDemandeFinancementContributionSharings(contributionId, demandeId, sharings, modules = [StoreModulesName.DEMANDES_FINANCEMENT, StoreModulesName.LATEST_DEMANDES_FINANCEMENT]) {
        // loop over demande list keys
        for (const module of modules) {
            // get the store module
            const store = this.storeService.get(module);
            // get the module data
            const data = store.data();
            // get demande financement to update
            const demande = data?.find(({ id }) => id === demandeId);
            if (demande) {
                // update contribution action
                const contribution = demande.awaitingActions?.find((action) => action.type === SollicitationsTypes.COMPLEMENTS && action.id === contributionId);
                if (contribution) {
                    // Keep demande message opened
                    this.demandesFinancementFormatter.setDemandeFinancementAwaitingActionsOpened(demande);
                    this.demandesFinancementFormatter.updateContributionSharingsAction(contribution, sharings);
                }
            }
        }
    }
    /**
     * Reactivate share action button + hide spinner
     * @param contributionId - contribution id to update action button
     * @param demandeId - demande financement id containing contribution to update
     * @param modules - store modules
     */
    unsetDemandeFinancementShareContributionActionLoading(contributionId, demandeId, modules = [StoreModulesName.DEMANDES_FINANCEMENT, StoreModulesName.LATEST_DEMANDES_FINANCEMENT]) {
        // loop over demande list keys
        for (const module of modules) {
            // get the store module
            const store = this.storeService.get(module);
            // get the module data
            const data = store.data();
            // get demande financement to update
            const demande = data?.find(({ id }) => id === demandeId);
            if (demande) {
                // update contribution action
                const contribution = demande.awaitingActions?.find(({ id }) => id === contributionId);
                if (contribution) {
                    // Keep demande message opened
                    this.demandesFinancementFormatter.setDemandeFinancementAwaitingActionsOpened(demande);
                    this.demandesFinancementFormatter.unsetShareContributionActionLoading(contribution);
                }
            }
        }
    }
    /**
     * Reactivate share action button + hide spinner
     * @param justificationId - contribution id to update action button
     * @param demandeId - demande financement id containing contribution to update
     * @param modules - store modules
     */
    unsetDemandeFinancementJustificationActionLoading(justificationId, demandeId, modules = [StoreModulesName.DEMANDES_FINANCEMENT, StoreModulesName.LATEST_DEMANDES_FINANCEMENT]) {
        // loop over demande list keys
        for (const module of modules) {
            // get the store module
            const store = this.storeService.get(module);
            // get the module data
            const data = store.data();
            // get demande financement to update
            const demande = data?.find(({ id }) => id === demandeId);
            if (demande) {
                // update contribution action
                const justification = demande.awaitingActions?.find((sollicitation) => sollicitation.type === SollicitationsTypes.JUSTIFICATIONS &&
                    sollicitation.justification.reference === justificationId);
                if (justification) {
                    // Keep demande message opened
                    this.demandesFinancementFormatter.setDemandeFinancementAwaitingActionsOpened(demande);
                    this.demandesFinancementFormatter.unsetJustificationActionLoading(justification);
                }
            }
        }
    }
    /**
     * Set isRestricted property of a demande
     */
    updateDemandeIsRestricted(demandeId, isRestricted) {
        const demande = this.storeService
            .getDemandesFinancement()
            .data()
            .find((demande) => demande.id === demandeId);
        if (demande)
            demande.isRestricted = isRestricted;
    }
    /**
     * Update a demande de financement
     *
     * @param demandeToUpdate - demande de financement to update
     * @returns updated demande de financement
     */
    async updateDemandeFinancement(demandeToUpdate) {
        const payload = await this.demandesFinancementAdapter.updateDemandeFinancement(demandeToUpdate);
        if (isResultError(payload)) {
            throw payload.error;
        }
        return null;
    }
    /**
     * Check if demande is restricted to current user
     *
     * @param demandeId - demande de financement id to check
     * @returns true if demande is restricted
     */
    async isDemandeRestrictedToUser(demandeId) {
        if (!demandeId)
            return false;
        const payload = await this.demandesFinancementAdapter.getDemandesFinancementRestricted();
        if (isResultSuccess(payload)) {
            return payload.response.includes(demandeId);
        }
        throw payload.error ?? new Error('an unknown error occurred');
    }
    /**
     * Create the ID from the reference
     *
     * @param reference - Demande de Financement reference
     * @returns the full id
     */
    buildIdFromReference(reference) {
        return `/referentiel-financement/api/tenants/${this.tenantId}/demandes-financement/` + reference;
    }
}
export { DemandesFinancementService };
//# sourceMappingURL=demandes-financement-service.js.map